import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api, SPORTS, UPI_QR_IMAGE, FEDERATION } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Copy, Check } from 'lucide-react';

const EMPTY = {
  full_name: '', dob: '', gender: 'Male', phone: '', email: '', address: '', district: '', pincode: '', state: '',
  sport: '', category: '', event: '', aadhaar: '', photo: '',
  guardian_name: '', guardian_phone: '', institution: '',
  utr: '', payment_screenshot: '',
};

async function fileToDataURL(file, maxKB = 900) {
  if (file.size > maxKB * 1024) throw new Error(`File must be under ${maxKB}KB`);
  return new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
}

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const copyUpi = () => {
    navigator.clipboard.writeText(FEDERATION.email).catch(() => {});
    navigator.clipboard.writeText('Q483398352@ybl');
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.dob || !form.phone || !form.email || !form.address || !form.district || !form.pincode || !form.state) return 'Fill all personal fields';
      if (!/^\d{10}$/.test(form.phone)) return 'Phone must be exactly 10 digits';
      if (!/^\d{6}$/.test(form.pincode)) return 'Pincode must be 6 digits';
      if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Enter a valid email';
    }
    if (step === 2) {
      if (!form.sport || !form.category) return 'Choose sport and category';
      if (form.aadhaar && !/^\d{12}$/.test(form.aadhaar)) return 'Aadhaar must be 12 digits';
      if (!form.photo) return 'Upload a passport photo';
    }
    if (step === 3) {
      if (!form.guardian_name) return "Guardian's name is required";
      if (form.guardian_phone && !/^\d{10}$/.test(form.guardian_phone)) return 'Guardian phone must be 10 digits';
    }
    if (step === 4) {
      if (!form.utr || form.utr.length < 6) return 'Enter UTR / Transaction ID (min 6 chars)';
      if (!form.payment_screenshot) return 'Upload the payment screenshot';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep(step + 1);
  };

  const submit = async () => {
    const err = validateStep(); if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/registrations', form);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Registration failed');
    } finally { setSubmitting(false); }
  };

  if (result) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] text-white pt-28 pb-24 grain" data-testid="register-success">
        <div className="mx-auto max-w-xl px-6 border border-[#CCFF00]/40 bg-[#141414] p-8 text-center">
          <div className="font-display text-6xl text-[#CCFF00] mb-3">✓</div>
          <h1 className="font-display uppercase text-3xl md:text-4xl tracking-tight">Application Submitted</h1>
          <p className="text-white/60 mt-3">Your application is now <b className="text-white">pending verification</b>. Save your Application Number.</p>
          <div className="mt-6 border border-dashed border-[#CCFF00]/60 py-5 font-mono text-2xl text-[#CCFF00]" data-testid="result-app-no">
            {result.application_no}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button onClick={() => navigate(`/track?app=${result.application_no}`)} className="bg-[#CCFF00] text-black py-3 font-mono text-xs uppercase tracking-widest hover:bg-white transition-colors" data-testid="go-track">Track Status</button>
            <button onClick={() => { setResult(null); setStep(1); setForm(EMPTY); }} className="border border-white/20 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white hover:text-black transition-colors" data-testid="register-again">New Application</button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-white pt-28 pb-24 grain" data-testid="register-page">
      <div className="mx-auto max-w-3xl px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-3">Player Registration</p>
          <h1 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tight mb-2">Join the Federation.</h1>
          <p className="text-white/60">4 quick steps · all levels welcome · instant application number.</p>
        </motion.div>

        {/* stepper */}
        <div className="mt-10 flex items-center gap-2" data-testid="stepper">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1">
              <div className={`h-1 ${step >= n ? 'bg-[#CCFF00]' : 'bg-white/15'}`} />
              <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-white/60">0{n} · {['Personal', 'Sport & Photo', 'Guardian', 'Payment'][n - 1]}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 border border-white/10 bg-[#141414] p-6 md:p-8 space-y-4">
          {step === 1 && (
            <>
              <Field label="Full name"><Input value={form.full_name} onChange={(e) => upd('full_name', e.target.value)} className="input-dark" data-testid="f-fullname" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth"><Input type="date" value={form.dob} onChange={(e) => upd('dob', e.target.value)} className="input-dark" data-testid="f-dob" /></Field>
                <Field label="Gender">
                  <Select value={form.gender} onValueChange={(v) => upd('gender', v)}>
                    <SelectTrigger className="input-dark" data-testid="f-gender"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-black border-white/20">
                      <SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone (10 digits)"><Input inputMode="numeric" maxLength={10} value={form.phone} onChange={(e) => upd('phone', e.target.value.replace(/\D/g, ''))} className="input-dark" data-testid="f-phone" /></Field>
                <Field label="Email"><Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} className="input-dark" data-testid="f-email" /></Field>
              </div>
              <Field label="Address"><Input value={form.address} onChange={(e) => upd('address', e.target.value)} className="input-dark" data-testid="f-address" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="District"><Input value={form.district} onChange={(e) => upd('district', e.target.value)} className="input-dark" data-testid="f-district" /></Field>
                <Field label="Pincode (6 digits)"><Input inputMode="numeric" maxLength={6} value={form.pincode} onChange={(e) => upd('pincode', e.target.value.replace(/\D/g, ''))} className="input-dark" data-testid="f-pincode" /></Field>
              </div>
              <Field label="State"><Input value={form.state} onChange={(e) => upd('state', e.target.value)} className="input-dark" data-testid="f-state" /></Field>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Sport">
                <Select value={form.sport} onValueChange={(v) => upd('sport', v)}>
                  <SelectTrigger className="input-dark" data-testid="f-sport"><SelectValue placeholder="Choose a sport" /></SelectTrigger>
                  <SelectContent className="bg-black border-white/20 max-h-72">
                    {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category / Weight"><Input value={form.category} onChange={(e) => upd('category', e.target.value)} placeholder="e.g. U-19, 65kg" className="input-dark" data-testid="f-category" /></Field>
                <Field label="Event (optional)"><Input value={form.event} onChange={(e) => upd('event', e.target.value)} className="input-dark" data-testid="f-event" /></Field>
              </div>
              <Field label="Aadhaar (12 digits, optional)"><Input inputMode="numeric" maxLength={12} value={form.aadhaar} onChange={(e) => upd('aadhaar', e.target.value.replace(/\D/g, ''))} className="input-dark" data-testid="f-aadhaar" /></Field>
              <Field label="Passport photo">
                <div className="flex items-center gap-3">
                  <div className="h-20 w-20 border border-white/20 bg-black overflow-hidden flex items-center justify-center">
                    {form.photo ? <img src={form.photo} alt="" className="h-full w-full object-cover" data-testid="f-photo-preview" /> : <span className="font-mono text-[9px] text-white/40">NO PHOTO</span>}
                  </div>
                  <label className="flex-1 cursor-pointer border border-dashed border-white/20 py-3 text-center font-mono text-[10px] uppercase tracking-widest hover:border-white">
                    {form.photo ? 'Change photo' : 'Upload photo'}
                    <input type="file" accept="image/*" className="hidden" data-testid="f-photo" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try { upd('photo', await fileToDataURL(f)); } catch (err) { toast.error(err.message); }
                    }} />
                  </label>
                </div>
              </Field>
            </>
          )}
          {step === 3 && (
            <>
              <Field label="Guardian / Father's name"><Input value={form.guardian_name} onChange={(e) => upd('guardian_name', e.target.value)} className="input-dark" data-testid="f-guardian" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Guardian phone (10 digits, optional)"><Input inputMode="numeric" maxLength={10} value={form.guardian_phone} onChange={(e) => upd('guardian_phone', e.target.value.replace(/\D/g, ''))} className="input-dark" data-testid="f-guardian-phone" /></Field>
                <Field label="School / College"><Input value={form.institution} onChange={(e) => upd('institution', e.target.value)} className="input-dark" data-testid="f-institution" /></Field>
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <div className="border border-[#CCFF00]/40 bg-black p-5 flex flex-col sm:flex-row gap-5 items-center">
                <img src={UPI_QR_IMAGE} alt="UPI QR" className="h-44 w-44 object-contain border border-white/10 bg-white p-1" data-testid="payment-qr" />
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#CCFF00] mb-1">Pay via PhonePe / Any UPI</p>
                  <div className="font-display text-xl break-all" data-testid="payment-upi">Q483398352@ybl</div>
                  <button type="button" onClick={copyUpi} className="mt-2 inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-white hover:text-black" data-testid="copy-upi">
                    {copied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy UPI</>}
                  </button>
                  <p className="text-xs text-white/50 mt-3">Scan the QR, pay, and enter your UTR / Transaction ID below along with the payment screenshot.</p>
                </div>
              </div>
              <Field label="UTR / Transaction ID"><Input value={form.utr} onChange={(e) => upd('utr', e.target.value)} className="input-dark" data-testid="f-utr" /></Field>
              <Field label="Payment screenshot">
                <div className="flex items-center gap-3">
                  <div className="h-20 w-20 border border-white/20 bg-black overflow-hidden flex items-center justify-center">
                    {form.payment_screenshot ? <img src={form.payment_screenshot} alt="" className="h-full w-full object-cover" data-testid="f-screenshot-preview" /> : <span className="font-mono text-[9px] text-white/40">NO FILE</span>}
                  </div>
                  <label className="flex-1 cursor-pointer border border-dashed border-white/20 py-3 text-center font-mono text-[10px] uppercase tracking-widest hover:border-white">
                    {form.payment_screenshot ? 'Change file' : 'Upload screenshot'}
                    <input type="file" accept="image/*" className="hidden" data-testid="f-screenshot" onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      try { upd('payment_screenshot', await fileToDataURL(f)); } catch (err) { toast.error(err.message); }
                    }} />
                  </label>
                </div>
              </Field>
            </>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <button type="button" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
                    className="font-mono text-xs uppercase tracking-widest text-white/60 disabled:opacity-30 hover:text-white" data-testid="btn-back">
              ← Back
            </button>
            {step < 4 ? (
              <button type="button" onClick={next} className="inline-flex items-center gap-2 bg-[#CCFF00] text-black px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white" data-testid="btn-next">
                Next <ArrowRight className="h-3 w-3" />
              </button>
            ) : (
              <button type="button" onClick={submit} disabled={submitting} className="bg-[#CCFF00] text-black px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-white disabled:opacity-50" data-testid="btn-submit">
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`.input-dark{background:#000;border-color:rgba(255,255,255,.2);border-radius:0;margin-top:4px}`}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</Label>
      {children}
    </div>
  );
}
